/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Media, MediaAlbumResponse } from '@whiteguru/capacitor-plugin-media';
import { ActionSheetController, AlertController, LoadingController, ToastButton, ToastController } from '@ionic/angular';
import { MediaItem, MediaItemDictionary } from 'src/models/media-item';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import write_blob from 'capacitor-blob-writer';
import { ApiService } from './api';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { Secretary } from 'src/models/secretary';
import { BehaviorSubject } from 'rxjs';
import { FilePicker, PickFilesResult } from '@capawesome/capacitor-file-picker';
import { Camera, CameraResultType, CameraSource, ImageOptions, Photo } from '@capacitor/camera';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { ImageService } from './image.service';

@Injectable({
    providedIn: 'root'
})
export class MediaService {
    selectedPhotoOrVideoGroup: any;
    selectedSecretary: Secretary;

    userEnabledScreenRecording = new BehaviorSubject<boolean>(false);

    btns = [
        {
            text: 'Upload a photo',
            role: 'image'
        },
        {
            text: 'Upload a file',
            role: 'file',
        },
        {
            text: 'Cancel',
            role: 'cancel'
        },
    ];

    constructor(
        private api: ApiService,
        private http: HttpClient,
        private loadingCtrl: LoadingController,
        private toast: ToastController,
        private actionSheet: ActionSheetController,
        private alert: AlertController,
        private imageService: ImageService
    ) { }

    async getUserPhotos(accountId: number, rodeoId: number = 0): Promise<MediaItemDictionary[]> {
        let url = `${this.api.endpoints.photo}/UserPhotos?accountId=${accountId}`;
        if (rodeoId > 0) {
            url += `&rodeoId=${rodeoId}`;
        }
        return await this.api.getWithAuth(url) as MediaItemDictionary[];
    }

    async getUserVideos(accountId: number, rodeoId: number = 0): Promise<MediaItemDictionary[]> {
        let url = `${this.api.endpoints.videos}/user-videos?accountId=${accountId}`;
        if (rodeoId > 0) {
            url += `&rodeoId=${rodeoId}`;
        }
        return await this.api.getWithAuth(url);
    }

    async getRunPhotos(runId: number): Promise<MediaItem[]> {
        return await this.api.getWithAuth(`${this.api.endpoints.photo}/run-photos?runId=${runId}`);
    }

    async getRunVideos(runId: number): Promise<MediaItem[]> {
        return await this.api.getWithAuth(`${this.api.endpoints.videos}/run-videos?runId=${runId}`);
    }

    async downloadMedia(media: any[], isVideo: boolean = false) {
        const label = isVideo ? 'video' : 'photo'
        const loading = await this.loadingCtrl.create({
            message: `Downloading ${label}${media.length > 1 ? 's' : ''}`,
        });

        await loading.present();

        let errorCount = 0;
        let successCount = 0;
        for (let i = 0; i < media.length; i++) {
            const photo = media[i];
            try {
                await this.saveMedia(photo.url, isVideo);
                loading.message = `Saving ${i + 1} of ${media.length} ${label}${media.length > 1 ? 's' : ''}`;
                successCount++;
            } catch (err) {
                errorCount++;
            }
        }

        await loading.dismiss();

        let message: string;
        if (errorCount > 0) {
            if (successCount === 0) {
                message = `Could not save ${label}s.`;
            } else {
                message = `Downloaded ${successCount} of ${media.length} ${label}s.`;
            }
        } else {
            message = `${media.length} ${label}s saved!`;
        }

        const toast = await this.toast.create({
            message,
            color: 'primary',
            duration: 3000,
            position: 'bottom'
        });
        await toast.present();
    }

    async saveMedia(mediaUrl: string, isVideo = false) {
        let path: string;
        const albumName = 'NextGenRodeo';
        const loading = await this.loadingCtrl.create({
            message: `Downloading ${isVideo ? 'video' : 'photo'}...`,
        });

        await loading.present();

        const isWeb = !Capacitor.isNativePlatform();
        try {
            path = await this.saveToFileSystem(mediaUrl, isWeb);
            if (isWeb) {
                return;
            }
            // console.log('SAVED TO FILE SYSTEM', path);
            const albumResponse: MediaAlbumResponse = await Media.getAlbums();
            // console.log('ALBUMS', albumResponse);
            const album = albumResponse.albums.find(x => x.name === albumName);
            // console.log('ALBUM', album);
            if (isVideo) {
                const resp = await Media.saveVideo({ path, album: { id: album?.identifier, name: album?.name || albumName } });
                // console.log('SAVED', resp);
            } else {
              const resp = await Media.savePhoto({ path, album: { id: album?.identifier, name: album?.name || albumName } });
              // console.log('SAVED', resp);
            }
            await this.deleteFile(path);
            //   this.insights.logEvent(this.insights.eventNames.runVideo.downloaded);
        } catch (err) {
            console.error(err);
            if (path && !isWeb) {
                await this.deleteFile(path);
            }
            throw new Error(err.message); //rethrow to handle ui in top component
            //   this.insights.logException(err);
        } finally {
            await loading.dismiss();
        }
    }

    async getBlobFromFile(filePath: string, type = 'image/jpeg') {
        const file = await this.readFilePath(filePath);
        return this.binaryStringToBlob(file.data as string, type);
    }

    generateFileName(orgName?: string, docName?: string) {
      let fileName = '';

      if (orgName) {
          const sanitizedName = orgName
          .replace(/[^a-z0-9]/gi, '')
          .replace(/\s+/g, '');

          fileName = sanitizedName;
      }

      if (docName) {
        const sanitizedName = docName
        .replace(/[^a-z0-9]/gi, '') // Remove non-alphanumeric characters
        .replace(/\s+/g, '');      // Remove any whitespace

          // There will not be an orgName when either TaxDoc or BirthCert
        if (sanitizedName === 'TaxDocument' || sanitizedName === 'BirthCertificate') {
            fileName = sanitizedName;
        } else {
            fileName += `/${sanitizedName}`;
        }
      }

      console.log('generated fileName', fileName);
      return fileName;
  }

    async showUploadOptions(fileName: string, accountId: number) {
        const selectedBtn = await this.presentUploadOptions();

        // console.log(selectedBtn);

        if (!selectedBtn.role || selectedBtn.role === 'cancel' || selectedBtn.role === 'backdrop') {
            return false;
        }

        try {
            if (selectedBtn.role === 'file') {
                const success = await this.handlePdfUpload(fileName, accountId);
                if (!success) {
                    return false;
                }
            } else {
                await this.handleImageUpload(fileName, accountId);
            }
            this.showToast('Document uploaded successfully!', 'success');
        } catch (err) {
            this.showToast('Could not upload document. Please try again later.', 'danger');
        }

        return true;
    }

    async presentUploadOptions(): Promise<{ text: string; role: string } | undefined> {
        const alertOrActionSheet = await this.alert.create({
          cssClass: 'file-upload',
          buttons: this.btns,
          header: 'Upload options'
        });

        await alertOrActionSheet.present();

        const result = await alertOrActionSheet.onDidDismiss();

        const selectedBtn = this.btns.find(btn => btn.role === result.role);

        return selectedBtn;
    }

    binaryStringToBlob(base64Data: string, type: string) {
        const sliceSize = 1024;
        const byteCharacters = atob(base64Data);
        const bytesLength = byteCharacters.length;
        const slicesCount = Math.ceil(bytesLength / sliceSize);
        const byteArrays = new Array(slicesCount);

        for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
            const begin = sliceIndex * sliceSize;
            const end = Math.min(begin + sliceSize, bytesLength);

            const bytes = new Array(end - begin);
            for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
                bytes[i] = byteCharacters[offset].charCodeAt(0);
            }
            byteArrays[sliceIndex] = new Uint8Array(bytes);
        }
        return new Blob(byteArrays, { type });
    }

    async handleImageUpload(fileName: string, accountId: number) {
      const isMobile = Capacitor.isNativePlatform();  
      const options: ImageOptions = {
            quality: 100,
            allowEditing: true,
            resultType: CameraResultType.Base64,
            source: isMobile ? CameraSource.Prompt : CameraSource.Photos,
            width: 1024,
            height: 1024,
        };

        let image: Photo;
        try {
          image = await Camera.getPhoto(options);
          // console.log('image', image);
        } catch (err) {
          if (!(err?.message as string).includes('cancel')) {
            this.showToast(err.message, 'danger', [{
              text: 'Open Device Settings',
              handler: () => {
                NativeSettings.open({
                  optionAndroid: AndroidSettings.ApplicationDetails, 
                  optionIOS: IOSSettings.App
                });
              }
            }]);
          }        
        }

        if (image) {
            const blob = this.binaryStringToBlob(image.base64String, image.format);

            await this.imageService.uploadDocument(blob, fileName, accountId);
    
            if (!fileName.endsWith(`.${image.format}`)) {
              fileName += `.${image.format}`;
            }
            return fileName;
        }
        
        return undefined;
    }

    async handlePdfUpload(fileName: string, accountId: number) {
        let result: PickFilesResult;
        try {
            result = await FilePicker.pickFiles({
                types: ['application/pdf'],
                limit: 1,
                readData: true
              });
        } catch (err) {
            console.error(err);
            //swallow this. it fails when people cancel
        }

        // console.log(result.files);

        if (result && result.files.length > 0) {
            const file = result.files[0];
            let blob = file.blob;

            if (Capacitor.isNativePlatform()) {
                const base64 = file.data;
                blob = this.binaryStringToBlob(base64, file.mimeType);
            }

            // console.log(blob);

            await this.imageService.uploadDocument(
                blob,
                fileName,
                accountId);
            
            if (!fileName.endsWith('.pdf')) {
              fileName += '.pdf';
            }
            
            return fileName;
        }

        return undefined;
    }

    private async deleteFile(path: string) {
        try {
            if (Capacitor.getPlatform() == 'android') {
                path = path.substring(path.indexOf('next-gen'));
            }
            await Filesystem.deleteFile({ path, directory: Directory.Data });
            // console.log('DELETED TEMP FILE');
        } catch (err) {
            console.error(err);
        }
    }

    private async saveToFileSystem(mediaUrl: string, isWeb: boolean) {
        const my_file_blob = await this.http.get(mediaUrl, { responseType: 'blob' }).toPromise();
        // console.log(my_file_blob);

        const mediaType = mediaUrl.split('.').pop();
        // console.log({ mediaType });

        if (isWeb) {
            this.downloadFileWeb(my_file_blob, mediaType);
            return;
        }

        const my_file_uri = await write_blob({
            path: `next-gen/temp.${mediaType}`,
            directory: Directory.Data,
            blob: my_file_blob,
            recursive: true,
            on_fallback: (error) => {
                console.warn(error);
            }
        });

        if (my_file_uri == null) {
            const filePath = await Filesystem.getUri({
                directory: Directory.Data,
                path: `next-gen/temp.${mediaType}`
            });

            return filePath.uri;
        }

        return my_file_uri;
    }

    private async downloadFileWeb(blob: any, type) {
        // const blob = new Blob([data], { type });
        const url= window.URL.createObjectURL(blob);
        window.open(url);
      }

    private async readFilePath(path: string) {
        // Here's an example of reading a file with a full file path. Use this to
        // read binary data (base64 encoded) from plugins that return File URIs, such as
        // the Camera.
        const contents = await Filesystem.readFile({
            path,
            encoding: Encoding.UTF8
        });
        return contents;
    };

    private showToast(customMessage: string, customColor: string, buttons: ToastButton[] = [{text: 'X'}]) {
        this.toast.create({
          message: customMessage,
          color: customColor,
          duration: 2500,
          buttons
        }).then(x => x.present());
    }
}
